import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Share2,
  Copy,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { aiAssistantService } from "@/services/aiAssistantService";

interface ShareInstanceDialogProps {
  instanceId: string;
  children?: React.ReactNode;
}

export const ShareInstanceDialog: React.FC<ShareInstanceDialogProps> = ({
  instanceId,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [publicId, setPublicId] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const checkStatus = async () => {
      if (!isOpen) return;
      setIsChecking(true);
      try {
        const { data, error } =
          await aiAssistantService.isInstancePublic(instanceId);
        if (error) throw error;
        if (data) {
          setIsPublic(data.isPublic);
          setPublicId(data.publicId);
        }
      } catch (e) {
        // swallow
      } finally {
        setIsChecking(false);
      }
    };
    checkStatus();
  }, [isOpen, instanceId]);

  const toggle = async (nextPublic: boolean) => {
    setIsLoading(true);
    try {
      if (nextPublic) {
        const { data, error } =
          await aiAssistantService.makeInstancePublic(instanceId);
        if (error) throw error;
        setIsPublic(true);
        setPublicId(data?.publicId);
        toast({
          title: "Assistant is now public",
          description: "Anyone with the link can use this assistant",
        });
      } else {
        const { error } =
          await aiAssistantService.makeInstancePrivate(instanceId);
        if (error) throw error;
        setIsPublic(false);
        setPublicId(undefined);
        toast({
          title: "Assistant is now private",
          description: "Public link disabled",
        });
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: e?.message || "Failed to update",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const url = publicId
    ? `${window.location.origin}/public/assistant/${publicId}`
    : "";

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copied", description: "Public link copied" });
    } catch {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Copy manually",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Share2 className="h-4 w-4" /> Share Assistant
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" /> Share Assistant (Instance)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Public Access</Label>
              <p className="text-xs text-gray-500">
                Allow anyone with the link to use this assistant
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isChecking ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              ) : (
                <>
                  {isPublic ? (
                    <Eye className="h-4 w-4 text-green-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  )}
                  <Switch
                    checked={isPublic}
                    onCheckedChange={toggle}
                    disabled={isLoading || isChecking}
                  />
                </>
              )}
            </div>
          </div>

          {isPublic && (
            <Badge variant="secondary" className="text-green-700 bg-green-50">
              Public - Anyone with the link
            </Badge>
          )}

          {isPublic && publicId && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" /> Public Link
                </Label>
                <div className="flex gap-2">
                  <Input value={url} readOnly className="font-mono text-xs" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copy}
                    className="shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => window.open(url, "_blank")}
                className="w-full flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" /> Open in New Tab
              </Button>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> This shares the assistant instance
              (interactive). Use the separate Share Output button to share a
              specific result (read-only).
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
